import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  IconButton,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../auth/useAuth';

// Types
interface MonitorThreshold {
  ThresholdID: number;
  BackstopID: number;
  FieldID: number;
  FieldName: string;
  SoftLower: number | null;
  HardLower: number | null;
  SoftUpper: number | null;
  HardUpper: number | null;
  RiskAggregator: number | null;
  RiskAggregatorName: string | null;
  UserName?: string;
  Timestamp?: string;
}

interface Fund {
  BackstopID: number;
  FundName: string;
}

interface MonitorField {
  FieldID: number;
  FieldName: string;
}

interface RiskIndicator {
  RiskIndicatorID: number;
  RiskIndicatorName: string;
}

interface MonthOption {
  date: string;
  label: string;
}

const ThresholdManagement: React.FC = () => {
  const { getAccessToken } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dataChanged, setDataChanged] = useState(false);
  
  // Data state
  const [funds, setFunds] = useState<Fund[]>([]);
  const [monitorFields, setMonitorFields] = useState<MonitorField[]>([]);
  const [riskIndicators, setRiskIndicators] = useState<RiskIndicator[]>([]);
  const [availableThresholds, setAvailableThresholds] = useState<MonitorThreshold[]>([]);
  const [monthOptions, setMonthOptions] = useState<MonthOption[]>([]);
  
  // Form state - matches Access form structure
  const [selectedFund, setSelectedFund] = useState<number | ''>('');
  const [selectedField, setSelectedField] = useState<number | ''>('');
  const [applyFromDate, setApplyFromDate] = useState<string>('');
  const [softLower, setSoftLower] = useState<string>('');
  const [hardLower, setHardLower] = useState<string>('');
  const [softUpper, setSoftUpper] = useState<string>('');
  const [hardUpper, setHardUpper] = useState<string>('');
  const [riskAggregator, setRiskAggregator] = useState<number | ''>('');

  // Load initial data
  useEffect(() => {
    loadFunds();
    loadMonitorFields();
    loadRiskIndicators();
  }, []);

  // Load month options when fund is selected (matches Access behavior)
  useEffect(() => {
    const loadMonthOptions = async () => {
      if (!selectedFund) {
        setMonthOptions([]);
        setApplyFromDate('');
        return;
      }
      
      try {
        const token = await getAccessToken();
        if (!token) {
          console.error('No access token available');
          return;
        }

        // Get all monitor dates for this fund (across all fields)
        const url = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-dates?backstop_id=${selectedFund}`;
        console.log('Loading monitor dates from:', url);

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const dates = await response.json();
          console.log('Received monitor dates:', dates);
          
          if (Array.isArray(dates) && dates.length > 0) {
            // Format dates to match the MonthOption interface
            const formattedDates = dates.map((d: any) => ({
              date: d.MonitorDate,
              label: new Date(d.MonitorDate).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
            }));
            
            setMonthOptions(formattedDates);
            setApplyFromDate(formattedDates[0].date);
            console.log('Set default date to:', formattedDates[0].date);
          } else {
            console.warn('No monitor data dates available for this fund');
            setMonthOptions([]);
            setApplyFromDate('');
          }
        } else {
          const errorText = await response.text();
          console.error('Failed to load monitor dates:', response.status, errorText);
          setMonthOptions([]);
        }
      } catch (err) {
        console.error('Error loading monitor dates:', err);
        setMonthOptions([]);
      }
    };

    loadMonthOptions();
  }, [selectedFund, getAccessToken]);

  // Load available thresholds when fund changes
  useEffect(() => {
    if (selectedFund) {
      loadAvailableThresholds();
    } else {
      setAvailableThresholds([]);
    }
  }, [selectedFund]);

  // Reload thresholds when applyFromDate changes (matches Access behavior)
  useEffect(() => {
    if (selectedFund && applyFromDate) {
      loadAvailableThresholds();
    }
  }, [applyFromDate]);

  // Update form when field is selected
  useEffect(() => {
    if (selectedField && selectedFund) {
      loadThresholdForField();
    } else {
      clearThresholdForm();
    }
  }, [selectedField]);

  const loadFunds = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/funds`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFunds(data.map((fund: any) => ({
          BackstopID: fund.BackstopID,
          FundName: fund.FundName
        })));
      }
    } catch (err) {
      console.error('Failed to load funds:', err);
    }
  };

  const loadMonitorFields = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-fields`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMonitorFields(data);
      }
    } catch (err) {
      console.error('Failed to load monitor fields:', err);
    }
  };

  const loadRiskIndicators = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/risk-indicators-list`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter out leverage (ID 6) as per Access VBA code
        setRiskIndicators(data.filter((ri: RiskIndicator) => ri.RiskIndicatorID !== 6));
      }
    } catch (err) {
      console.error('Failed to load risk indicators:', err);
    }
  };

  const loadAvailableThresholds = async () => {
    if (!selectedFund) return;
    
    setLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) return;

      // Build the URL with threshold_date parameter to get thresholds active at the selected date
      let url = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-thresholds?backstop_id=${selectedFund}`;
      
      // If applyFromDate is set, use it to get thresholds active at that date
      // Otherwise, use newest=1 to get current thresholds
      if (applyFromDate) {
        url += `&threshold_date=${applyFromDate}`;
      } else {
        url += `&newest=1`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableThresholds(data);
      }
    } catch (err) {
      console.error('Failed to load thresholds:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadThresholdForField = async () => {
    if (!selectedField || !selectedFund) return;

    try {
      const token = await getAccessToken();
      if (!token) return;

      // Build URL with threshold_date parameter to get threshold active at the selected date
      // This matches the loadAvailableThresholds behavior
      let url = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-thresholds?backstop_id=${selectedFund}&field_id=${selectedField}`;
      
      if (applyFromDate) {
        url += `&threshold_date=${applyFromDate}`;
      } else {
        url += `&newest=1`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          const threshold = data[0];
          setSoftLower(threshold.SoftLower !== null ? threshold.SoftLower.toString() : '');
          setHardLower(threshold.HardLower !== null ? threshold.HardLower.toString() : '');
          setSoftUpper(threshold.SoftUpper !== null ? threshold.SoftUpper.toString() : '');
          setHardUpper(threshold.HardUpper !== null ? threshold.HardUpper.toString() : '');
          setRiskAggregator(threshold.RiskAggregator || '');
        } else {
          clearThresholdForm();
        }
      }
    } catch (err) {
      console.error('Failed to load threshold:', err);
    }
  };

  const clearThresholdForm = () => {
    setSoftLower('');
    setHardLower('');
    setSoftUpper('');
    setHardUpper('');
    setRiskAggregator('');
  };

  const handleFundChange = async (fundId: number | '') => {
    // Check for unsaved changes
    if (dataChanged) {
      if (window.confirm('You have unsaved changes. Do you want to save them before changing funds?')) {
        await handleSave();
      }
    }
    
    setSelectedFund(fundId);
    setSelectedField('');
    clearThresholdForm();
    setDataChanged(false);
  };

  const handleFieldChange = (fieldId: number | '') => {
    setSelectedField(fieldId);
    setDataChanged(false);
  };

  const handleThresholdClick = (threshold: MonitorThreshold) => {
    setSelectedField(threshold.FieldID);
    setSoftLower(threshold.SoftLower !== null ? threshold.SoftLower.toString() : '');
    setHardLower(threshold.HardLower !== null ? threshold.HardLower.toString() : '');
    setSoftUpper(threshold.SoftUpper !== null ? threshold.SoftUpper.toString() : '');
    setHardUpper(threshold.HardUpper !== null ? threshold.HardUpper.toString() : '');
    setRiskAggregator(threshold.RiskAggregator || '');
  };

  const validateThresholds = (): boolean => {
    const sl = softLower ? parseFloat(softLower) : null;
    const hl = hardLower ? parseFloat(hardLower) : null;
    const su = softUpper ? parseFloat(softUpper) : null;
    const hu = hardUpper ? parseFloat(hardUpper) : null;

    // Check soft lower < hard lower
    if (sl !== null && hl !== null && sl < hl) {
      setError('Soft Lower must be greater than or equal to Hard Lower');
      return false;
    }

    // Check soft upper > hard upper
    if (su !== null && hu !== null && su > hu) {
      setError('Soft Upper must be less than or equal to Hard Upper');
      return false;
    }

    // Check soft lower > soft upper
    if (sl !== null && su !== null && sl > su) {
      setError('Soft Lower must be less than or equal to Soft Upper');
      return false;
    }

    // Check that at least upper or lower is set
    const hasLower = sl !== null || hl !== null;
    const hasUpper = su !== null || hu !== null;
    
    if (!hasLower && !hasUpper) {
      setError('You must set at least one upper or lower threshold');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!selectedFund || !selectedField) {
      setError('Please select a fund and risk indicator');
      return;
    }

    if (!validateThresholds()) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');

      const payload: any = {
        BackstopID: selectedFund,
        FieldID: selectedField,
        SoftLower: softLower ? parseFloat(softLower) : null,
        HardLower: hardLower ? parseFloat(hardLower) : null,
        SoftUpper: softUpper ? parseFloat(softUpper) : null,
        HardUpper: hardUpper ? parseFloat(hardUpper) : null,
        RiskAggregator: riskAggregator || null
      };

      // Add ApplyFromDate if specified
      if (applyFromDate) {
        payload.ApplyFromDate = applyFromDate;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-thresholds`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save threshold');
      }

      setSuccess('Threshold saved successfully');
      setDataChanged(false);
      
      // Reload the available thresholds list
      await loadAvailableThresholds();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save threshold');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedFund || !selectedField) {
      setError('Please select a fund and risk indicator');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this threshold?')) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');

      // Get the threshold ID
      const threshold = availableThresholds.find(
        t => t.FieldID === selectedField && t.BackstopID === selectedFund
      );

      if (!threshold) {
        throw new Error('Threshold not found');
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-thresholds/${threshold.ThresholdID}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete threshold');
      }

      setSuccess('Threshold deleted successfully');
      clearThresholdForm();
      setSelectedField('');
      
      // Reload the available thresholds list
      await loadAvailableThresholds();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete threshold');
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (value: number | null) => {
    if (value === null) return '';
    
    // If the value is a whole number, display without decimals
    if (Number.isInteger(value)) {
      return value.toString();
    }
    
    // Otherwise, display with 2 decimal places
    return value.toFixed(2);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
          Update Monitoring Threshold
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadAvailableThresholds()}
            disabled={loading || !selectedFund}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Fund Selection and Apply From Date */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Fund Name</InputLabel>
                  <Select
                    value={selectedFund}
                    onChange={(e) => handleFundChange(e.target.value as number | '')}
                    label="Fund Name"
                  >
                    <MenuItem value="">Select a fund...</MenuItem>
                    {funds.map(fund => (
                      <MenuItem key={fund.BackstopID} value={fund.BackstopID}>
                        {fund.FundName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Apply From</InputLabel>
                  <Select
                    value={applyFromDate}
                    onChange={(e) => setApplyFromDate(e.target.value)}
                    label="Apply From"
                    disabled={!selectedFund}
                  >
                    {monthOptions.map(month => (
                      <MenuItem key={month.date} value={month.date}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Update Threshold Section */}
        {selectedFund && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: '#1a237e' }}>
                Update Threshold
              </Typography>

              <Grid container spacing={1.5} alignItems="center">
                {/* All fields in one row on desktop, matching Access layout */}
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Risk Indicator</InputLabel>
                    <Select
                      value={selectedField}
                      onChange={(e) => handleFieldChange(e.target.value as number | '')}
                      label="Risk Indicator"
                    >
                      <MenuItem value="">Select a field...</MenuItem>
                      {monitorFields.map(field => (
                        <MenuItem key={field.FieldID} value={field.FieldID}>
                          {field.FieldName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} md={1.2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Soft Lower"
                    type="number"
                    value={softLower}
                    onChange={(e) => {
                      setSoftLower(e.target.value);
                      setDataChanged(true);
                    }}
                    disabled={!selectedField}
                    inputProps={{ step: 0.01 }}
                  />
                </Grid>

                <Grid item xs={6} md={1.2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Hard Lower"
                    type="number"
                    value={hardLower}
                    onChange={(e) => {
                      setHardLower(e.target.value);
                      setDataChanged(true);
                    }}
                    disabled={!selectedField}
                    inputProps={{ step: 0.01 }}
                  />
                </Grid>

                <Grid item xs={6} md={1.2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Soft Upper"
                    type="number"
                    value={softUpper}
                    onChange={(e) => {
                      setSoftUpper(e.target.value);
                      setDataChanged(true);
                    }}
                    disabled={!selectedField}
                    inputProps={{ step: 0.01 }}
                  />
                </Grid>

                <Grid item xs={6} md={1.2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Hard Upper"
                    type="number"
                    value={hardUpper}
                    onChange={(e) => {
                      setHardUpper(e.target.value);
                      setDataChanged(true);
                    }}
                    disabled={!selectedField}
                    inputProps={{ step: 0.01 }}
                  />
                </Grid>

                <Grid item xs={12} md={4.2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Risk Aggregator</InputLabel>
                    <Select
                      value={riskAggregator === '' ? '' : riskAggregator}
                      onChange={(e) => {
                        const value = e.target.value;
                        setRiskAggregator(value === '' ? '' : Number(value));
                        setDataChanged(true);
                      }}
                      label="Risk Aggregator"
                      disabled={!selectedField}
                    >
                      <MenuItem value="">None</MenuItem>
                      {riskIndicators.map(ri => (
                        <MenuItem key={ri.RiskIndicatorID} value={ri.RiskIndicatorID}>
                          {ri.RiskIndicatorName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Available Fields List */}
        {selectedFund && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: '#1a237e' }}>
                Available Fields
              </Typography>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Field Name</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Soft Lower</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Hard Lower</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Soft Upper</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Hard Upper</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Risk Aggregator</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {availableThresholds.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No thresholds found for this fund
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        availableThresholds.map((threshold) => (
                          <TableRow
                            key={threshold.FieldID}
                            hover
                            onClick={() => handleThresholdClick(threshold)}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: selectedField === threshold.FieldID ? '#e3f2fd' : 'inherit'
                            }}
                          >
                            <TableCell>{threshold.FieldName}</TableCell>
                            <TableCell align="right">{formatValue(threshold.SoftLower)}</TableCell>
                            <TableCell align="right">{formatValue(threshold.HardLower)}</TableCell>
                            <TableCell align="right">{formatValue(threshold.SoftUpper)}</TableCell>
                            <TableCell align="right">{formatValue(threshold.HardUpper)}</TableCell>
                            <TableCell>{threshold.RiskAggregatorName || ''}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
          disabled={!selectedField || loading}
          color="error"
        >
          Delete Threshold
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!selectedField || loading || !dataChanged}
          sx={{ backgroundColor: '#1a237e' }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default ThresholdManagement;
